/**
 * The account-key checkboxes shared by Link repository and Edit (spec "Edit screen"). Each key reads as
 * a person would say it: the warehouse icon and type, the database in bold, the host, and how many
 * accounts read it. The raw `TYPE|host|database` shows only in a tooltip, with a copy button.
 */
import { KeyRound } from 'lucide-react';
import { useId } from 'react';

import { CopyButton } from '@/_shared/components/CopyButton';
import { TruncateWithTooltip } from '@/_shared/components/TruncateWithTooltip';
import { Badge } from '@/_shared/components/ui/badge';
import { Checkbox } from '@/_shared/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';
import { cn } from '@/_shared/lib/utils';

import type { AccountKeyDisplay, AccountKeyOption } from '../../lib/linkedRepoList';
import { warehouseTypeMeta } from '../warehouseAccount/warehouseTypes';

/** A key's readable label: icon, database (bold) and "Type · host". Every part truncates on one line. */
export function AccountKeyLabel({
  display,
  className,
}: {
  display: AccountKeyDisplay;
  className?: string;
}): React.JSX.Element {
  const meta = warehouseTypeMeta(display.type);
  const secondary = display.host && display.host !== display.database ? `${meta.label} · ${display.host}` : meta.label;
  return (
    <span className={cn('flex min-w-0 items-center gap-2', className)}>
      <meta.Icon className="size-4 shrink-0" />
      <span className="flex min-w-0 flex-col">
        <TruncateWithTooltip focusable className="text-sm font-semibold text-foreground">
          {display.database}
        </TruncateWithTooltip>
        <TruncateWithTooltip focusable className="text-xs text-muted-foreground">
          {secondary}
        </TruncateWithTooltip>
      </span>
    </span>
  );
}

/** "Used by N accounts", with the names on hover. */
function UsedBy({ accounts }: { accounts: readonly { id: string; name: string }[] }): React.JSX.Element {
  const label = accounts.length === 1 ? 'Used by 1 account' : `Used by ${accounts.length} accounts`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className="shrink-0 rounded-sm text-xs text-muted-foreground underline-offset-2 focus-ring hover:underline"
        >
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent>{accounts.map((a) => a.name).join(', ')}</TooltipContent>
    </Tooltip>
  );
}

/** The raw key, in a tooltip, and a copy button for it. */
export function RawAccountKey({ accountKey, database }: { accountKey: string; database: string }): React.JSX.Element {
  return (
    <span className="flex shrink-0 items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            aria-label={`Account key for ${database}: ${accountKey}`}
            className="inline-flex rounded-sm text-muted-foreground focus-ring"
          >
            <KeyRound className="size-3.5" aria-hidden />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm font-mono break-all">{accountKey}</TooltipContent>
      </Tooltip>
      <CopyButton
        text={accountKey}
        copyLabel="Account key"
        variant="tertiary"
        iconOnly
        aria-label={`Copy account key for ${database}`}
      />
    </span>
  );
}

export function AccountKeyPicker({
  options,
  value,
  onChange,
  disabled = false,
  id,
  // `aria-invalid` is not valid on a group: the error reaches the group through `aria-describedby`.
  'aria-invalid': _ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: {
  options: readonly AccountKeyOption[];
  value: readonly string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** Wired by a `FormControl`. */
  id?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}): React.JSX.Element {
  const baseId = useId();
  const selected = new Set(value);
  const toggle = (key: string, checked: boolean): void => {
    if (checked) onChange([...value, key]);
    else onChange(value.filter((k) => k !== key));
  };

  if (options.length === 0) {
    return (
      <p className="rounded-lg border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">
        No warehouse account yet. Add one in Settings → Warehouses first.
      </p>
    );
  }

  return (
    // A `div` group, not a `ul`: `role="group"` on a list would leave each item with no list parent.
    <div
      id={id}
      role="group"
      aria-label="Account keys"
      aria-describedby={ariaDescribedBy}
      className="flex max-h-72 flex-col divide-y overflow-y-auto rounded-lg border p-0.5"
    >
      {options.map((option, index) => {
        const id = `${baseId}-${index}`;
        const checked = selected.has(option.accountKey);
        // A key no account has can only be unticked: there is nothing to build its docs from.
        const locked = disabled || (option.removed && !checked);
        return (
          <div key={option.accountKey} className="flex min-w-0 items-center gap-3 px-3 py-2.5">
            <Checkbox
              id={id}
              checked={checked}
              disabled={locked}
              aria-label={option.display.database}
              // Several keys can share a database name; the host tells them apart for a screen reader.
              aria-describedby={`${id}-host`}
              onCheckedChange={(next) => toggle(option.accountKey, next === true)}
            />
            <span id={`${id}-host`} className="sr-only">
              {[warehouseTypeMeta(option.display.type).label, option.display.host].filter(Boolean).join(' · ')}
            </span>
            <label htmlFor={id} className={cn('flex min-w-0 flex-1 cursor-pointer', locked && 'cursor-not-allowed')}>
              <AccountKeyLabel display={option.display} />
            </label>
            {option.removed ? (
              <Badge variant="warning" className="shrink-0">
                Removed
              </Badge>
            ) : (
              <UsedBy accounts={option.accounts} />
            )}
            <RawAccountKey accountKey={option.accountKey} database={option.display.database} />
          </div>
        );
      })}
    </div>
  );
}
